import React from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Divider,
  Link,
  Stack,
  Button,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  HeadsetMic as HeadsetMicIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import logo from "../images/LogoNavbar1.svg";

function Footer() {
  const navigate = useNavigate();

  const handleWhatsAppClick = () => {
    window.location.href = "https://wa.me/message/HOYXZTTFNXNZH1";
  };

  const socialIcons = [
    { icon: <FacebookIcon />, link: "https://facebook.com" },
    { icon: <InstagramIcon />, link: "https://instagram.com" },
    { icon: <TwitterIcon />, link: "https://twitter.com" },
    { icon: <YouTubeIcon />, link: "https://youtube.com" },
  ];

  return (
    <Box sx={{ mt: 6 }}>
      <Box
        sx={{
          background: "linear-gradient(-135deg, #275469 0%, #19242F 100%)",
          color: "#fff",
          p: { xs: 4, md: 6 },
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: "0 -4px 30px rgba(0,0,0,0.3)",
        }}
      >
        <Grid
          container
          spacing={4}
          alignItems="stretch"
          justifyContent="center"
        >
          {/* Sección Logo, Slogan y Navegación */}
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
              }}
              onClick={handleWhatsAppClick}
            >
              <Box
                component="img"
                src={logo}
                alt="WebProfit"
                sx={{ height: 60 }}
              />
              <Typography sx={{ fontWeight: 900, color: "#fff", fontSize: 30 }}>
                WebProfit
              </Typography>
            </Box>

            <Typography
              sx={{ fontWeight: 500, fontSize: 19, textAlign: "center" }}
            >
              Impulsamos tu negocio
            </Typography>

            <Box sx={{ mt: 2, width: "100%" }}>
              <Stack spacing={0.5} alignItems="center">
                {[
                  { label: "Inicio", action: () => navigate("/") },
                  { label: "Servicios", action: () => navigate("/servicios") },
                  {
                    label: "Contáctanos",
                    action: () => {
                      const target = document.getElementById("sabermas");
                      if (target) target.scrollIntoView({ behavior: "smooth" });
                      else navigate("/contacto");
                    },
                  },
                ].map((btn, i) => (
                  <Button
                    key={i}
                    onClick={btn.action}
                    variant="text"
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      letterSpacing: 1,
                      fontSize: "0.95rem",
                      textTransform: "uppercase",
                      position: "relative",
                      "&:after": {
                        content: '""',
                        position: "absolute",
                        width: "0%",
                        height: "2px",
                        bottom: 0,
                        left: "50%",
                        backgroundColor: "#ffd700",
                        transition: "all 0.3s ease",
                        transform: "translateX(-50%)",
                      },
                      "&:hover:after": {
                        width: "80%",
                      },
                      "&:hover": {
                        color: "#ffd700",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {btn.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Grid>
          <Grid
            item
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                backgroundColor: "rgba(255,255,255,0.4)",
                height: "100%",
              }}
            />
          </Grid>
          {/* Sección Redes Sociales */}
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            textAlign="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: 1,
                textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
              }}
            >
              Seguinos
            </Typography>
            <Box
              sx={{ mt: 4, display: "flex", gap: 1, justifyContent: "center" }}
            >
              {socialIcons.map((social, i) => (
                <IconButton
                  key={i}
                  href={social.link}
                  target="_blank"
                  sx={{
                    color: "#fff",
                    background: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      color: "#ffd700",
                      background: "rgba(255,255,255,0.3)",
                      transform: "scale(1.3)",
                      boxShadow: "0 0 10px #275469",
                      transition: "all 0.3s ease",
                    },
                    borderRadius: "50%",
                    p: 2,
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>
          <Grid
            item
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                backgroundColor: "rgba(255,255,255,0.4)",
                height: "100%",
              }}
            />
          </Grid>
          {/* Sección Atención al Cliente */}
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            textAlign="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            {/* Encabezado con ícono */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                justifyContent: "center",
              }}
            >
              <IconButton
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #19242F 0%, #275469 100%)",
                  boxShadow: "0 4px 20px rgba(255, 255, 255, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #275469, #19242F)",
                    transform: "scale(1.1)",
                    boxShadow: "0 0 15px rgba(255,215,0,0.7)",
                  },
                }}
              >
                <HeadsetMicIcon sx={{ fontSize: 30, color: "#fff" }} />
              </IconButton>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 1,
                    textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
                  }}
                >
                  Atención al cliente
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "0.9rem",
                    color: "rgba(255,255,255,0.8)",
                  }}
                >
                  Estamos para ayudarte 24/7
                </Typography>
              </Box>
            </Box>

            {/* Teléfono */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                p: 1,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.05)",
                width: "100%",
                justifyContent: "center",
              }}
            >
              <PhoneIcon sx={{ color: "#ffd700", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 500 }}>
                +54 260 4206967 - Gianfranco
              </Typography>
            </Box>

            {/* Ubicación */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
                p: 1,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.05)",
                width: "100%",
                justifyContent: "center",
              }}
            >
              <LocationOnIcon sx={{ fontSize: 22, color: "#ffd700" }} />
              <Typography sx={{ fontWeight: 500 }}>
                Mendoza - San Rafael
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default Footer;
